import React, { useEffect } from 'react';
import { ToastProvider } from '../ToastContext';
import {
  TriggerUpdateProvider,
  useTriggerUpdate,
} from '../TriggerUpdateContext';
import { useDispatch, useSelector } from 'react-redux';
import { getClinicianProfileBasic, setClinicianTeams } from '../../store/ai2';
import RolesProvider from '../RoleContext';
import ModalProvider from '../ModalContext';
import LoggedInUserProvider from '../LoggedInUserContext';
import { useGlobalLoading } from '../LoadingContext';
import usePreferences from '../../ai2/pages/Settings/hook/usePreferences';
import type { RootState } from '../../store';
import EnvironmentProvider from '../EnvironmentContext';
import { currentSession, getUserAccess } from '../../auth/AuthState';
import { useGetTeams } from '../../ai2/components/teams/hooks/useGetTeams';

interface Props {
  children: React.ReactNode;
}
const GlobalStore = ({ children }: Props) => {
  const [initialized, setInitialized] = React.useState(false);
  const dispatch = useDispatch();
  const { selectedTenancy, clinics } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const { updateClinicianProfileCount } = useTriggerUpdate();
  const { startLoading, stopLoading } = useGlobalLoading();
  const { getAllPreferences } = usePreferences();
  const { postGetTeams } = useGetTeams();

  const getClinicianProfile = getClinicianProfileBasic();

  const fetchAndStoreClinicianTeams = async () => {
    const hospitalIds = Array.isArray(clinics)
      ? Array.from(
          new Set(
            clinics
              .filter((c) => c?.organisation_unit === 'hospital')
              .map((c) => c.clinic_id)
              .filter((id): id is number => typeof id === 'number'),
          ),
        )
      : [];
    if (!hospitalIds.length) {
      return;
    }
    const teams = await postGetTeams(hospitalIds);
    dispatch(setClinicianTeams(teams));
  };

  // Only call when the app initializes
  const getInitialData = async () => {
    await Promise.all([getAllPreferences(), fetchAndStoreClinicianTeams()]);
    setInitialized(true);
  };

  const checkCurrentUserAvailable = async () => {
    try {
      const session = await currentSession();
      if (session) {
        const userAccess = await getUserAccess();
        if (userAccess.userStatus === 1) {
          return true;
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    (async () => {
      const isAvailable = await checkCurrentUserAvailable();
      if (!isAvailable) return;
      startLoading();
      await getClinicianProfile(dispatch);
      stopLoading();
    })();
  }, [updateClinicianProfileCount]);

  useEffect(() => {
    if (!selectedTenancy || initialized) return;
    getInitialData();
  }, [selectedTenancy]);

  return (
    <EnvironmentProvider>
      <LoggedInUserProvider>
        <TriggerUpdateProvider>
          <ModalProvider>
            <ToastProvider>
              <RolesProvider>{children}</RolesProvider>
            </ToastProvider>
          </ModalProvider>
        </TriggerUpdateProvider>
      </LoggedInUserProvider>
    </EnvironmentProvider>
  );
};

export default GlobalStore;
